import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, 
  Plus, 
  Search, 
  Eye,
  CheckCircle,
  PauseCircle,
  XCircle,
  TrendingUp,
  DollarSign,
  Calendar
} from 'lucide-react';
import axios from 'axios';

const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || 'http://localhost:8001';

const Abonnements = () => {
  const [abonnements, setAbonnements] = useState([]);
  const [stats, setStats] = useState({
    total_abonnements: 0,
    abonnements_actifs: 0,
    abonnements_suspendus: 0,
    abonnements_resilies: 0,
    ca_mensuel_recurrent: 0,
    ca_annuel_previsionnel: 0,
    taux_retention: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadAbonnements();
    loadStats();
  }, [filterStatut, filterType]);

  const loadAbonnements = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      if (filterStatut !== 'all') params.statut = filterStatut;
      if (filterType !== 'all') params.type_prestation = filterType;

      const response = await axios.get(`${API_URL}/api/abonnements`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAbonnements(response.data);
    } catch (error) {
      console.error('Erreur chargement abonnements:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/abonnements/stats/general`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'actif':
        return <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center gap-1"><CheckCircle size={12} /> Actif</span>;
      case 'suspendu':
        return <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full flex items-center gap-1"><PauseCircle size={12} /> Suspendu</span>;
      case 'resilie':
        return <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full flex items-center gap-1"><XCircle size={12} /> Résilié</span>;
      case 'expire':
        return <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">Expiré</span>;
      default:
        return <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">{statut}</span>;
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      'surveillance': '📹 Surveillance',
      'gardiennage': '🛡️ Gardiennage',
      'ronde': '🚶 Rondes',
      'intervention': '🚨 Intervention',
      'telepointage': '📱 Télépointage',
      'videosurveillance': '📷 Vidéosurveillance',
      'alarme': '🔔 Alarme',
      'autre': '📋 Autre'
    };
    return types[type] || type;
  };

  const getPeriodiciteLabel = (periodicite) => {
    const labels = {
      'mensuel': 'Mensuel',
      'trimestriel': 'Trimestriel',
      'semestriel': 'Semestriel',
      'annuel': 'Annuel'
    };
    return labels[periodicite] || periodicite;
  };

  const filteredAbonnements = abonnements.filter(abo =>
    abo.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    abo.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    abo.designation?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <RefreshCw className="h-8 w-8 text-purple-600" />
            Abonnements & Contrats Récurrents
          </h1>
          <p className="text-gray-600 mt-1">Gestion des contrats clients à facturation récurrente</p>
        </div>
        <button
          className="bg-purple-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-purple-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Nouvel Abonnement
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md border-l-4 border-l-green-500 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Abonnements Actifs</div>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-green-600">{stats.abonnements_actifs}</div>
          <div className="text-xs text-gray-500 mt-1">sur {stats.total_abonnements} total</div>
        </div>

        <div className="bg-white rounded-lg shadow-md border-l-4 border-l-blue-500 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">CA Mensuel Récurrent</div>
            <DollarSign className="h-5 w-5 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-blue-600">
            {stats.ca_mensuel_recurrent?.toFixed(0) || '0'}€
          </div>
          <div className="text-xs text-gray-500 mt-1">MRR (Monthly Recurring Revenue)</div>
        </div>

        <div className="bg-white rounded-lg shadow-md border-l-4 border-l-purple-500 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">CA Annuel Prévisionnel</div>
            <TrendingUp className="h-5 w-5 text-purple-500" />
          </div>
          <div className="text-3xl font-bold text-purple-600">
            {stats.ca_annuel_previsionnel?.toFixed(0) || '0'}€
          </div>
          <div className="text-xs text-gray-500 mt-1">ARR (Annual Recurring Revenue)</div>
        </div>

        <div className="bg-white rounded-lg shadow-md border-l-4 border-l-indigo-500 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Taux de Rétention</div>
            <Calendar className="h-5 w-5 text-indigo-500" />
          </div>
          <div className="text-3xl font-bold text-indigo-600">{stats.taux_retention}%</div>
          <div className="text-xs text-gray-500 mt-1">Clients fidélisés</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Rechercher (N°, client, désignation)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">Tous types</option>
            <option value="surveillance">Surveillance</option>
            <option value="gardiennage">Gardiennage</option>
            <option value="ronde">Rondes</option>
            <option value="intervention">Intervention</option>
            <option value="videosurveillance">Vidéosurveillance</option>
            <option value="alarme">Alarme</option>
          </select>

          <select
            value={filterStatut}
            onChange={(e) => setFilterStatut(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">Tous statuts</option>
            <option value="actif">Actif</option>
            <option value="suspendu">Suspendu</option>
            <option value="resilie">Résilié</option>
            <option value="expire">Expiré</option>
          </select>
        </div>
      </div>

      {/* Liste des abonnements */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement...</div>
        ) : filteredAbonnements.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <RefreshCw className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun abonnement trouvé</p>
            <button className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">
              Créer le premier abonnement
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">N° Abonnement</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Client</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Type</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Désignation</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Périodicité</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Montant TTC</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Date début</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Statut</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredAbonnements.map((abo) => (
                  <tr key={abo.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono font-medium">{abo.numero}</td>
                    <td className="px-4 py-3 text-sm font-medium">{abo.client_name}</td>
                    <td className="px-4 py-3 text-sm">{getTypeLabel(abo.type_prestation)}</td>
                    <td className="px-4 py-3 text-sm">{abo.designation}</td>
                    <td className="px-4 py-3 text-sm">{getPeriodiciteLabel(abo.periodicite)}</td>
                    <td className="px-4 py-3 text-sm font-bold text-purple-600">
                      {abo.montant_ttc?.toFixed(2) || '0.00'}€
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(abo.date_debut).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">{getStatutBadge(abo.statut)}</td>
                    <td className="px-4 py-3">
                      <button
                        className="p-1 hover:bg-purple-50 rounded"
                        title="Voir détails"
                      >
                        <Eye size={16} className="text-purple-600" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6">
        <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Revenus Récurrents (MRR/ARR)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-purple-800">
          <div>
            <p className="font-medium mb-1">💰 MRR (Monthly Recurring Revenue):</p>
            <p className="text-2xl font-bold text-purple-600">
              {stats.ca_mensuel_recurrent?.toFixed(2) || '0.00'}€/mois
            </p>
          </div>
          <div>
            <p className="font-medium mb-1">📊 ARR (Annual Recurring Revenue):</p>
            <p className="text-2xl font-bold text-purple-600">
              {stats.ca_annuel_previsionnel?.toFixed(2) || '0.00'}€/an
            </p>
          </div>
          <div>
            <p className="font-medium mb-1">📈 Taux de rétention:</p>
            <p className="text-2xl font-bold text-purple-600">
              {stats.taux_retention}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Abonnements;
